import { useCallback, useState } from "react"

import useDateFormatters from "@hooks/useDateFormatters"
import useEditingLock from "@hooks/useEditingLock"

import { faIcon } from "@utils/faIcon"
import formatRecurrence from "@utils/formatRecurrence"

import { CollaborationRoom } from "@particles"

import { IconButton } from "@atoms"

import { ClientLocationCard, EditingUserAvatar, PreviewModule, ValueDisplay } from "@molecules"
import UserAvatar from "@molecules/UserAvatar/UserAvatar"

import JobSeriesEditDialog from "@pages/JobSeries/JobSeriesDetails/JobSeriesEditDialog/JobSeriesEditDialog"

import styles from "./JobSeriesDetailsPanel.module.scss"
import { JobSeriesDetailsPanelProps } from "./JobSeriesDetailsPanel.types"

function JobSeriesDetailsPanelContent(props: JobSeriesDetailsPanelProps) {
    const { jobSeries, onJobSeriesUpdate } = props
    const { formatDate } = useDateFormatters()
    const isLoading = !jobSeries

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

    const formId = `job-series-${jobSeries?.id}`
    const { isLocked, lockingUser, startEditing, stopEditing } = useEditingLock(formId)

    const isRollingSeries = jobSeries?.type === "rolling"

    const handleOpenDialog = useCallback(() => {
        startEditing()
        setIsEditDialogOpen(true)
    }, [startEditing])

    const handleCloseDialog = useCallback(
        (open: boolean) => {
            if (!open) {
                stopEditing()
            }
            setIsEditDialogOpen(open)
        },
        [stopEditing],
    )

    const handleEditSuccess = useCallback(() => {
        stopEditing()
        onJobSeriesUpdate?.()
    }, [stopEditing, onJobSeriesUpdate])

    const renderDetailsCta = useCallback(() => {
        if (!isRollingSeries) {
            return undefined
        }

        if (isLocked && lockingUser) {
            return <EditingUserAvatar user={lockingUser} size="md" />
        }

        return (
            <IconButton
                icon={faIcon({ name: "pen", style: "regular" })}
                size="md"
                variant="ghost"
                aria-label="Edit details"
                onClick={handleOpenDialog}
            />
        )
    }, [isRollingSeries, isLocked, lockingUser, handleOpenDialog])

    return (
        <div className={styles.base}>
            <ClientLocationCard
                clientId={jobSeries?.client_id ?? ""}
                clientName={jobSeries?.client_name ?? ""}
                clientTypeLabel={jobSeries?.client_type_label}
                serviceLocationName={jobSeries?.service_location_name ?? ""}
                serviceLocationAddress={jobSeries?.service_location_address ?? ""}
                isLoading={isLoading}
            />

            <PreviewModule label="Details" cta1={renderDetailsCta()} contentClassName={styles.detailsContent}>
                <ValueDisplay label="Service" value={jobSeries?.service_name} isLoading={isLoading} />
                <ValueDisplay label="Generation mode" value={jobSeries?.type_label} isLoading={isLoading} />
                <ValueDisplay
                    label="Series start date"
                    value={formatDate(jobSeries?.recurrence_start)}
                    isLoading={isLoading}
                />
                <ValueDisplay
                    label="Recurrence"
                    value={
                        jobSeries
                            ? formatRecurrence({
                                  interval: jobSeries.recurrence_interval,
                                  frequency: jobSeries.recurrence_frequency,
                                  startDate: jobSeries.recurrence_start,
                                  weeklyRecurrence: jobSeries.weekly_recurrence,
                                  monthlyRecurrence: jobSeries.monthly_recurrence,
                              })
                            : undefined
                    }
                    isLoading={isLoading}
                />
                <ValueDisplay
                    label="Series end date"
                    value={formatDate(jobSeries?.recurrence_end)}
                    isLoading={isLoading}
                />
                <ValueDisplay label="Created at" value={formatDate(jobSeries?.created_at)} isLoading={isLoading} />
                <ValueDisplay
                    label="Created by"
                    isLoading={isLoading}
                    value={
                        jobSeries?.created_by ? (
                            <div className={styles.createdByValue}>
                                <UserAvatar user={jobSeries.created_by} size="xs" />
                                <span>{jobSeries.created_by.full_name}</span>
                            </div>
                        ) : undefined
                    }
                />
            </PreviewModule>

            {jobSeries && isRollingSeries && (
                <JobSeriesEditDialog
                    jobSeries={jobSeries}
                    isOpen={isEditDialogOpen}
                    onOpenChange={handleCloseDialog}
                    onSuccess={handleEditSuccess}
                />
            )}
        </div>
    )
}

export default function JobSeriesDetailsPanel(props: JobSeriesDetailsPanelProps) {
    const { jobSeries } = props
    const roomName = jobSeries?.id != null ? `job-series-${jobSeries.id}` : undefined

    return (
        <CollaborationRoom roomName={roomName}>
            <JobSeriesDetailsPanelContent {...props} />
        </CollaborationRoom>
    )
}
